// services/printReportService.js

// Enhanced message formatting function
const formatMessageContent = (content) => {
  if (!content) return content;

  // Handle line breaks
  let formattedContent = content.replace(/\\n/g, '\n').replace(/\n/g, '<br/>');

  // Enhanced styling for semantic classes
  formattedContent = formattedContent.replace(
    /<([^>]*class="[^"]*sectionHeader[^"]*"[^>]*)>/g,
    '<$1 class="bg-gradient-to-br from-primary_hover to-primary text-white p-4 my-5 rounded-xl font-bold text-lg text-center shadow-lg relative overflow-hidden">'
  );

  formattedContent = formattedContent.replace(
    /<([^>]*class="[^"]*listItem[^"]*"[^>]*)>/g,
    '<$1 class="bg-slate-50 border border-slate-200 border-l-4 border-l-teal-500 rounded-lg p-3 my-2 block transition-all shadow-sm">'
  );

  formattedContent = formattedContent.replace(
    /<([^>]*class="[^"]*content[^"]*"[^>]*)>/g,
    '<$1 class="text-gray-700 my-4 p-4 bg-white rounded-lg border border-gray-200 leading-relaxed shadow-sm">'
  );

  formattedContent = formattedContent.replace(
    /<([^>]*class="[^"]*important[^"]*"[^>]*)>/g,
    '<$1 class="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg p-2 my-1 mr-2 font-semibold inline-block text-sm shadow-md border-0">'
  );

  // Enhanced list styling with Tailwind
  formattedContent = formattedContent.replace(
    /<ul class='list'>/g,
    '<ul class="list-none p-0 my-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 border border-slate-200 shadow-sm">'
  );

  formattedContent = formattedContent.replace(
    /<ol class='list'>/g,
    '<ol class="pl-5 my-4 bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 pl-9 border border-slate-200 shadow-sm counter-reset-[custom-counter]">'
  );

  formattedContent = formattedContent.replace(
    /<li class='listItem'>/g,
    '<li class="bg-white border border-slate-200 border-l-4 border-l-teal-500 rounded-lg p-3 my-2 block transition-all shadow-sm relative pl-10">'
  );

  // Handle markdown elements with Tailwind
  formattedContent = formattedContent.replace(/\*\*(.*?)\*\*/g, '<strong class="text-teal-500 font-bold">$1</strong>');
  formattedContent = formattedContent.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em class="text-gray-600 italic">$1</em>');
  formattedContent = formattedContent.replace(/__(.*?)__/g, '<u class="underline text-teal-500">$1</u>');
  formattedContent = formattedContent.replace(/~~(.*?)~~/g, '<del class="line-through text-gray-400">$1</del>');

  // Enhanced code blocks
  formattedContent = formattedContent.replace(/```(.*?)```/gs, '<pre class="bg-gradient-to-br from-slate-100 to-slate-200 border border-slate-300 rounded-lg p-4 my-3 overflow-x-auto font-mono shadow-inner"><code class="text-slate-800">$1</code></pre>');
  formattedContent = formattedContent.replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-2 py-1 rounded font-mono text-teal-500 border border-slate-200">$1</code>');

  // Enhanced headers with Tailwind
  formattedContent = formattedContent.replace(/^### (.*$)/gm, '<h3 class="text-lg font-semibold my-4 text-teal-500 border-b-2 border-gray-200 pb-1">$1</h3>');
  formattedContent = formattedContent.replace(/^## (.*$)/gm, '<h2 class="text-xl font-bold my-5 text-gray-800 relative pl-3 border-l-4 border-teal-500">$1</h2>');
  formattedContent = formattedContent.replace(/^# (.*$)/gm, '<h1 class="text-3xl font-bold font-black my-8 text-gray-900 text-center p-5 bg-gradient-to-br from-teal-500 to-primary text-white rounded-2xl shadow-lg relative overflow-hidden drop-shadow-md"><span class="relative z-10">$1</span><div class="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-to-br from-white/10 to-transparent transform rotate-45"></div></h1>');

  // Enhanced lists with Tailwind
  formattedContent = formattedContent.replace(/^[\s]*[-*]\s(.*)$/gm, '<li class="my-2 p-3 pl-10 relative text-gray-700 leading-relaxed bg-white rounded-lg border-l-4 border-teal-500 shadow-sm"><span class="absolute left-4 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-gradient-to-br from-teal-500 to-primary rounded-full shadow-md"></span>$1</li>');

  formattedContent = formattedContent.replace(/^[\s]*\d+\.\s(.*)$/gm, '<li class="my-2 p-3 text-gray-700 leading-relaxed bg-white rounded-lg border-l-4 border-teal-500 shadow-sm counter-increment-[custom-counter] relative pl-12"><span class="absolute left-4 top-1/2 -translate-y-1/2 w-6 h-6 bg-gradient-to-br from-teal-500 to-primary text-white rounded-full flex items-center justify-center font-semibold text-xs shadow-md before:content-[counter(custom-counter)]"></span>$1</li>');

  // Enhanced links
  formattedContent = formattedContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-teal-500 no-underline font-medium border-b border-dotted border-teal-500 transition-all px-1 py-0.5 rounded hover:border-solid hover:bg-teal-50" target="_blank">$1</a>');

  return formattedContent;
};

// Medical report formatting for academic style
const formatMedicalReport = (content) => {
  if (!content) return content;

  // Handle line breaks
  let formattedContent = content.replace(/\\n/g, '\n').replace(/\n/g, '<br/>');

  // Academic table formatting
  formattedContent = formattedContent.replace(
    /<table[^>]*>/g,
    '<div class="academic-table-wrapper my-6"><table class="academic-table w-full border-collapse bg-white shadow-sm">'
  );
  formattedContent = formattedContent.replace(/<\/table>/g, '</table></div>');

  // Medical section headers - academic style
  formattedContent = formattedContent.replace(
    /<([^>]*class="[^"]*sectionHeader[^"]*"[^>]*)>/g,
    '<$1 class="academic-section bg-slate-600 text-white text-center py-3 px-4 font-bold text-base mb-0">'
  );

  // Medical content blocks - academic paper style
  formattedContent = formattedContent.replace(
    /<([^>]*class="[^"]*content[^"]*"[^>]*)>/g,
    '<$1 class="academic-content bg-white p-6 text-gray-800 leading-relaxed border-l-4 border-slate-400 my-4">'
  );

  // Important medical information - academic highlight
  formattedContent = formattedContent.replace(
    /<([^>]*class="[^"]*important[^"]*"[^>]*)>/g,
    '<$1 class="academic-highlight bg-slate-50 border border-slate-300 text-slate-800 px-4 py-2 my-2 inline-block font-medium">'
  );

  // Academic list styling
  formattedContent = formattedContent.replace(
    /<ul class='list'>/g,
    '<ul class="academic-list my-4 bg-white border border-gray-200">'
  );

  formattedContent = formattedContent.replace(
    /<ol class='list'>/g,
    '<ol class="academic-numbered-list my-4 bg-white border border-gray-200 counter-reset-[academic-counter]">'
  );

  formattedContent = formattedContent.replace(
    /<li class='listItem'>/g,
    '<li class="academic-list-item bg-gray-50 p-4 border-b border-gray-100 text-gray-700">'
  );

  // Academic text formatting
  formattedContent = formattedContent.replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900 font-bold">$1</strong>');
  formattedContent = formattedContent.replace(/(?<!\*)\*([^*\n]+?)\*(?!\*)/g, '<em class="text-gray-700 italic">$1</em>');
  formattedContent = formattedContent.replace(/__(.*?)__/g, '<u class="underline decoration-slate-500 decoration-2">$1</u>');

  // Academic code blocks
  formattedContent = formattedContent.replace(/```(.*?)```/gs, 
    '<div class="academic-code-block bg-gray-100 border border-gray-300 p-4 my-4 font-mono text-sm">' +
    '<pre class="text-gray-800 whitespace-pre-wrap leading-relaxed">$1</pre></div>'
  );

  // Academic headers - research paper style
  formattedContent = formattedContent.replace(/^### (.*$)/gm, 
    '<h3 class="academic-h3 text-lg font-bold text-gray-900 mt-8 mb-4 pb-2 border-b-2 border-gray-300 uppercase tracking-wider">$1</h3>'
  );
  formattedContent = formattedContent.replace(/^## (.*$)/gm, 
    '<h2 class="academic-h2 text-xl font-bold text-gray-900 mt-10 mb-5 text-center pb-3 border-b-3 border-slate-500 uppercase tracking-wider">$1</h2>'
  );
  formattedContent = formattedContent.replace(/^# (.*$)/gm, 
    '<h1 class="academic-h1 text-2xl font-bold text-center text-gray-900 bg-gray-50 border-2 border-gray-300 p-6 mb-8 uppercase tracking-wider">$1</h1>'
  );

  // Academic list items
  formattedContent = formattedContent.replace(/^[\s]*[-*]\s(.*)$/gm, 
    '<li class="academic-bullet py-2 px-4 border-b border-gray-100 text-gray-700 leading-relaxed">• $1</li>'
  );

  formattedContent = formattedContent.replace(/^[\s]*\d+\.\s(.*)$/gm, 
    '<li class="academic-numbered py-2 px-4 border-b border-gray-100 text-gray-700 leading-relaxed counter-increment-[academic-counter]"><span class="font-bold text-slate-600 mr-2" data-counter></span>$1</li>'
  );

  // Professional academic links
  formattedContent = formattedContent.replace(/\[([^\]]+)\]\(([^)]+)\)/g, 
    '<a href="$2" class="text-slate-600 hover:text-slate-800 font-medium underline" target="_blank">$1</a>'
  );

  return formattedContent;
};

// Print service
export const printReportService = {
  printReport: (report, patient) => {
    const printContent = formatMessageContent(report.summaryHtml);
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Medical Report - ${patient?.fullName}</title>
        <style>
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
          }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            line-height: 1.6;
            color: #374151;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
          }
          .print-header {
            text-align: center;
            margin-bottom: 30px;
            padding: 20px;
            background: linear-gradient(135deg, #14b8a6, #0f766e);
            color: white;
            border-radius: 12px;
          }
          .print-content {
            background: white;
            padding: 30px;
            border-radius: 12px;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          }
          h1, h2, h3 { color: #0f766e; }
          .medical-content { animation: none; }
        </style>
      </head>
      <body>
        <div class="print-header">
          <h1>📋 Medical Report Analysis</h1>
          <p><strong>Patient:</strong> ${patient?.fullName}</p>
          <p><strong>Patient ID:</strong> ${patient?.id}</p>
          <p><strong>Generated:</strong> ${new Date(report.createdAt).toLocaleDateString()}</p>
        </div>
        <div class="print-content">
          ${printContent}
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  },

  printEnhancedReport: (report, patient, contentElementId = 'historical-medical-report-content') => {
    const printContent = document.getElementById(contentElementId);
    if (printContent) {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Medical Report - ${patient?.fullName}</title>
          <style>
            @media print {
              * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; }
            }
            body {
              font-family: 'Times New Roman', Georgia, serif;
              line-height: 1.6;
              color: #374151;
              max-width: 210mm;
              margin: 0 auto;
              padding: 15mm;
              background: white;
            }
            .academic-header {
              text-align: center;
              margin-bottom: 20px;
              padding: 15px;
              background: #475569;
              color: white;
              page-break-inside: avoid;
            }
            .academic-content { 
              background: white;
              border: 1px solid #d1d5db;
              font-family: 'Times New Roman', Georgia, serif;
              font-size: 12pt;
              line-height: 1.5;
            }
            .patient-info-table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              border: 1px solid #374151;
            }
            .patient-info-table th {
              background: #475569;
              color: white;
              padding: 8px 12px;
              text-align: center;
              font-weight: bold;
              font-size: 11pt;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }
            .patient-info-table td {
              padding: 8px 12px;
              border: 1px solid #d1d5db;
              font-size: 10pt;
            }
            .patient-info-table tr:nth-child(even) td {
              background: #f8fafc;
            }
            .academic-table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
              font-size: 10pt;
              border: 1px solid #374151;
            }
            .academic-table th {
              background: #475569 !important;
              color: white !important;
              padding: 8px 6px;
              text-align: center;
              font-weight: bold;
              font-size: 9pt;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              border: 1px solid #374151;
            }
            .academic-table td {
              padding: 6px 8px;
              border: 1px solid #d1d5db;
              text-align: center;
              font-size: 9pt;
            }
            .academic-table tr:nth-child(even) {
              background: #f8fafc !important;
            }
            @page { 
              margin: 15mm; 
              size: A4;
            }
          </style>
        </head>
        <body>
          <div class="academic-header">
            <h1 style="margin: 0; font-size: 18pt; text-transform: uppercase; letter-spacing: 1px;">Medical Analysis Report</h1>
            <p style="margin: 8px 0 0 0; font-size: 11pt;">Clinical Data Analysis & Assessment</p>
          </div>
          
          <table class="patient-info-table">
            <thead>
              <tr>
                <th colspan="4">Patient Information</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style="background: #f8fafc;"><strong>Patient Name:</strong></td>
                <td>${patient?.fullName || 'N/A'}</td>
                <td style="background: #f8fafc;"><strong>Patient ID:</strong></td>
                <td>${patient?.id || 'N/A'}</td>
              </tr>
              <tr>
                <td style="background: #f8fafc;"><strong>Report Date:</strong></td>
                <td>${report ? new Date(report.createdAt).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  }) : new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}</td>
                <td style="background: #f8fafc;"><strong>Analysis Time:</strong></td>
                <td>${report ? new Date(report.createdAt).toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    hour12: true 
                  }) : new Date().toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    hour12: true 
                  })}</td>
              </tr>
            </tbody>
          </table>
          
          <div class="academic-content">
            ${printContent.innerHTML}
          </div>
          
          <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 9pt;">
            <div style="margin-bottom: 10px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px;">
              Medical Disclaimer
            </div>
            <p style="margin: 8px 0; line-height: 1.4;">
              This report is generated by artificial intelligence for informational and analytical purposes only. 
              All medical decisions should be made in consultation with qualified healthcare professionals. 
              This analysis does not substitute professional medical advice, diagnosis, or treatment.
            </p>
            <div style="margin-top: 15px; padding-top: 10px; border-top: 1px solid #d1d5db; font-size: 8pt;">
              Generated by DrEasy AI Medical Intelligence System | 
              Report ID: DR-${patient?.id}-${report ? new Date(report.createdAt).toISOString().slice(0,10) : new Date().toISOString().slice(0,10)}
            </div>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  }
};